import { computed, getCurrentInstance, Ref } from "@vue/composition-api";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { MessageAttachment } from "@scrile/api-provider/dist/api/MessagesProvider";
import { isImageFile, isVideoFile } from "@/lib/fileHelpers";

export const getDuration = (file: MessageAttachment | ContentFile | FileData) =>
  Number("file" in file ? file.file.metadata.duration : file.metadata.duration);

export default function useLockedFileInfo(files: Ref<Array<MessageAttachment | ContentFile>>) {
  const getFile = (file: MessageAttachment | ContentFile) => ("file" in file ? file.file : file);

  const isAllFilesVideos = computed(() => files.value.every(a => isVideoFile(getFile(a))));
  const isAllFilesImages = computed(() => files.value.every(a => isImageFile(getFile(a))));
  const isFilesWithVideo = computed(() => files.value.find(file => isVideoFile(getFile(file))));
  const icon = computed(() => {
    if (isAllFilesVideos.value) return "sci-play";
    if (isAllFilesImages.value) return "sci-image";
    return "sci-file-multiple";
  });
  const showDuration = computed(() => {
    return isAllFilesVideos.value || (!isAllFilesImages.value && !isAllFilesVideos.value);
  });
  const totalUnlockedFilesDuration = computed(() => {
    if (!showDuration.value || !isFilesWithVideo.value) return "";
    const result = files.value.reduce((acc, attachment) => {
      if (isVideoFile(getFile(attachment))) acc += getDuration(attachment);
      return acc;
    }, 0);
    return String(result);
  });
  const vm = getCurrentInstance();
  const lockedAttachmentsCount = computed<string | undefined>(() => {
    if (isAllFilesVideos.value) {
      return vm?.$tc("video", files.value.length);
    }
    if (isAllFilesImages.value) {
      return vm?.$tc("photo", files.value.length);
    }
    return `${vm?.$tc("photo", files.value.filter(i => isImageFile(getFile(i))).length)} & ${vm?.$tc(
      "video",
      files.value.filter(i => isVideoFile(getFile(i))).length
    )}
      `;
  });

  return {
    icon,
    lockedAttachmentsCount,
    totalUnlockedFilesDuration,
  };
}
